import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

/**
 * A modal dialog, accessible on purpose rather than by accident.
 *
 * There was none in this codebase before, and the request that produced it
 * asked for an *accessible* profile pop-up — so the accessibility is the
 * feature, not a polish pass afterwards. Each piece below is here because
 * leaving it out breaks the dialog for somebody specific:
 *
 *   * **`role="dialog"` + `aria-modal`** — tells a screen reader that the rest
 *     of the page is inert. Without it the reader keeps announcing the rail
 *     behind, and a blind user has no way to tell they are "inside" anything.
 *   * **`aria-labelledby` pointing at the real heading** — the dialog
 *     announces itself by its own title instead of as an unnamed group. The id
 *     comes from `useId`, so two dialogs on one page cannot collide.
 *   * **Focus moves in on open, and back to the trigger on close.** The
 *     return journey is the half that gets forgotten: without it a keyboard
 *     user who closes the dialog is dumped at the top of the document and has
 *     to tab all the way back to where they were.
 *   * **Tab is cycled inside.** A dialog you can tab out of, while the
 *     background is announced inert, puts focus somewhere the user cannot see
 *     and cannot get back from.
 *   * **Escape closes it**, which is the shortcut everybody tries first.
 *   * **Background scroll is locked**, because a modal that scrolls the page
 *     underneath loses the reader's place.
 *   * **The backdrop closes on click but not on drag-release.** `mousedown`
 *     and `mouseup` must BOTH land on the backdrop, so selecting text inside
 *     the dialog and releasing outside it does not throw the form away. This
 *     is the one that bites people filling in a long field.
 *
 * Motion is omitted deliberately rather than guarded: `DESIGN.md` requires
 * `prefers-reduced-motion` to remove transform and opacity motion, and the
 * honest way to honour that for a panel which appears instantly is not to
 * animate it at all. Nothing here to disable, nothing to get wrong.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One line under the heading. Announced with the dialog. */
  description?: string;
  children: ReactNode;
  /** Actions. Kept out of the scroll area so they never scroll away. */
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const descriptionId = useId();
  /** Where focus came from, so it can go back there. */
  const returnTo = useRef<HTMLElement | null>(null);
  /**
   * Did this click *start* on the backdrop?
   *
   * Both halves of the click must land there, or selecting text inside the
   * dialog and releasing outside it would throw the form away — the defect
   * that bites whoever is filling in a long field. Declared up here with the
   * other refs rather than beside its handlers, because everything below the
   * `if (!open) return null` is a conditional hook call and React refuses it.
   */
  const downOnBackdrop = useRef(false);

  const focusables = useCallback((): HTMLElement[] => {
    if (!panel.current) return [];
    return Array.from(
      panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }, []);

  // Remember the trigger, move focus in, and put it back on the way out.
  useEffect(() => {
    if (!open) return;

    returnTo.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // The first real control, or the panel itself when there is none — never
    // the close button by default, which would read the dialog out backwards.
    const first = focusables();
    const target = first.find((el) => el.dataset.autofocus === "true") ?? first[0];
    (target ?? panel.current)?.focus();

    return () => {
      // Guarded: the trigger may have unmounted while the dialog was open
      // (a row that disappeared on save), in which case there is nowhere to
      // return to and the browser's default is better than an exception.
      const back = returnTo.current;
      if (back && document.contains(back)) back.focus();
    };
  }, [open, focusables]);

  // Escape, and Tab cycling.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;

      // Wrap at both ends, and also catch the case where focus has somehow
      // escaped the panel already — then Tab brings it back rather than
      // walking further away.
      if (!panel.current?.contains(active as Node)) {
        e.preventDefault();
        first.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose, focusables]);

  // Scroll lock. The previous value is restored rather than cleared, so a
  // page that was already locked by something else stays locked.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      // The overlay never scrolls. It is the viewport minus its own
      // padding, and the panel inside is capped to it — so the dialog
      // always fits the screen and the only thing that can ever scroll is
      // the body, and only when its content genuinely does not fit.
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-ink/60 p-4 sm:p-6"
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
        downOnBackdrop.current = false;
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        // A flex column capped at the overlay's height: header and footer
        // keep their size, the body takes what is left. `max-h-full`
        // rather than a vh figure, because the overlay has already
        // subtracted its own padding and a hard 70vh does not know about
        // that — it was both too tall to fit and too short to need.
        className="flex max-h-full w-full max-w-2xl flex-col border border-line-strong bg-panel shadow-xl outline-none"
      >
        <div className="flex shrink-0 items-start justify-between gap-6 border-b border-line px-6 py-5">
          <div className="min-w-0">
            <h2
              id={headingId}
              className="font-display text-2xl font-medium tracking-[-0.03em] text-ink"
            >
              {title}
            </h2>
            {description && (
              <p
                id={descriptionId}
                className="mt-1.5 max-w-[62ch] text-xs leading-[1.1rem] text-muted"
              >
                {description}
              </p>
            )}
          </div>
          {/* A real button with a real accessible name. An × with no label is
              the single most common dialog defect there is. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 border border-line px-2.5 py-1 text-base leading-none text-muted transition-colors duration-150 ease-damped hover:border-line-strong hover:text-ink"
          >
            <span aria-hidden>&times;</span>
          </button>
        </div>

        {/* `min-h-0` is load-bearing: a flex child defaults to min-height
            auto, which refuses to shrink below its content and would push
            the panel past the screen however it is capped. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-line px-6 py-5">{footer}</div>
        )}
      </div>
    </div>
  );
}
